(() => {
  const normalize = (value) => String(value || '').trim().toLowerCase();

  const imageMeta = (img) => {
    if (!img) return { file: '', version: 0, alt: '' };
    const src = img.getAttribute('src') || '';
    const file = decodeURIComponent(src.split('?')[0].split('/').pop() || '');
    const match = src.match(/[?&]v=(\d+)/);
    return {
      file,
      version: match ? Number(match[1]) : 0,
      alt: img.getAttribute('alt') || '',
    };
  };

  const colorForImage = (img, anchors) => {
    const meta = imageMeta(img);
    const alt = normalize(meta.alt);
    const altTokens = alt.split(/[^a-z0-9áàâãéêíóôõúç]+/i).filter(Boolean);
    const byAlt = anchors.find((anchor) => {
      const color = normalize(anchor.color);
      return color && (alt === color || altTokens.includes(color));
    });
    if (byAlt) return byAlt.color;

    const byFile = anchors.find((anchor) => anchor.file === meta.file && Number(anchor.version) === meta.version);
    if (byFile) return byFile.color;

    const byName = anchors.find((anchor) => anchor.file && anchor.file === meta.file);
    if (byName) return byName.color;

    if (!meta.version) return null;

    let best = null;
    let bestDistance = Infinity;
    anchors.forEach((anchor) => {
      if (!anchor.version) return;
      const distance = Math.abs(meta.version - Number(anchor.version));
      if (distance < bestDistance) {
        bestDistance = distance;
        best = anchor.color;
      }
    });
    return best;
  };

  const buildColorMap = (config) => {
    const anchors = (config.anchors || []).map((anchor) => ({
      ...anchor,
      version: Number(anchor.version) || 0,
    }));
    const media = Array.isArray(config.media) ? config.media : [];

    media.forEach((item) => {
      const anchor = anchors.find((entry) => String(entry.mediaId) === String(item.id));
      if (anchor && !anchor.version && Number(item.version)) anchor.version = Number(item.version);
    });

    const usable = anchors.filter((anchor) => anchor.version > 0);
    const map = new Map();
    const source = media.length ? media : anchors.map((anchor) => ({ id: anchor.mediaId, version: anchor.version }));

    source.forEach((item) => {
      const direct = anchors.find((anchor) => String(anchor.mediaId) === String(item.id));
      if (direct) {
        map.set(String(item.id), direct.color);
        return;
      }

      const version = Number(item.version) || 0;
      if (!version || usable.length < 2) return;

      let best = null;
      let bestDistance = Infinity;
      usable.forEach((anchor) => {
        const distance = Math.abs(version - anchor.version);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = anchor.color;
        }
      });
      if (best) map.set(String(item.id), best);
    });

    return map;
  };

  const slideMatches = (slide, colorByMediaId, anchors, selectedColor) => {
    const mediaNode = slide.querySelector('[data-media-id]');
    const mediaId = mediaNode?.getAttribute('data-media-id');
    if (mediaId && colorByMediaId.has(mediaId)) {
      return normalize(colorByMediaId.get(mediaId)) === normalize(selectedColor);
    }

    const color = colorForImage(slide.querySelector('img'), anchors);
    if (!color) return colorByMediaId.size === 0;
    return normalize(color) === normalize(selectedColor);
  };

  const replaceHandlerIndex = (value, index) => value.replace(/\/(\d+)\s*$/, `/${index}`);

  const reindexHandlers = (nodes) => {
    nodes.forEach((node, index) => {
      ['on:click', 'on:pointerenter'].forEach((name) => {
        const value = node.getAttribute(name);
        if (value) node.setAttribute(name, replaceHandlerIndex(value, index));
      });
    });
  };

  const updateTimeline = (element, value) => {
    const style = element.getAttribute('style') || '';
    if (!style.includes('--slideshow-timeline')) return;
    element.setAttribute('style', style.replace(/--slideshow-timeline:\s*[^;]+/, `--slideshow-timeline: ${value}`));
  };

  const hydrateFromDom = (config, root) => {
    if (!config || !root?.querySelectorAll) return;
    root.querySelectorAll('[data-laeva-media-sources] img').forEach((img) => {
      const version = imageMeta(img).version;
      const mediaId = img.getAttribute('data-media-id');
      if (!version || !mediaId) return;
      (config.anchors || []).forEach((anchor) => {
        if (String(anchor.mediaId) === mediaId) anchor.version = version;
      });
      (config.media || []).forEach((item) => {
        if (String(item.id) === mediaId && !Number(item.version)) item.version = version;
      });
    });
  };

  const readConfig = (root, gallery) => {
    const scope = gallery.closest('.shopify-section, dialog') || root;
    const script = scope.querySelector('script[data-laeva-variant-media]');
    if (!script || !script.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  };

  const filterGallery = (gallery, config) => {
    if (gallery.getAttribute('data-laeva-ready') === 'true') return;

    const anchors = Array.isArray(config?.anchors) ? config.anchors : [];
    const selectedColor = config?.selectedColor;
    const colorByMediaId = buildColorMap(config);
    if (anchors.length < 2 || !selectedColor || colorByMediaId.size === 0) {
      gallery.setAttribute('data-laeva-ready', 'true');
      return;
    }

    const slides = [...gallery.querySelectorAll('slideshow-slide')];
    const mask = slides.map((slide) => slideMatches(slide, colorByMediaId, anchors, selectedColor));
    const kept = mask.filter(Boolean).length;
    if (slides.length && kept === 0) {
      gallery.setAttribute('data-laeva-ready', 'true');
      gallery.setAttribute('data-laeva-color', selectedColor);
      return;
    }

    if (slides.length) {
      gallery.querySelectorAll('slideshow-controls').forEach((controls) => {
        const buttons = [...controls.querySelectorAll('button')].filter((button) =>
          /\/select\/\d+\s*$/.test(button.getAttribute('on:click') || '')
        );
        if (buttons.length === mask.length) {
          buttons.forEach((button, index) => {
            if (!mask[index]) button.remove();
          });
        }
      });

      const zoomItems = [...gallery.querySelectorAll('.dialog-zoomed-gallery > li')];
      if (zoomItems.length === mask.length) {
        zoomItems.forEach((item, index) => {
          if (!mask[index]) item.remove();
        });
      }

      const dialogThumbs = [...gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail')];
      if (dialogThumbs.length === mask.length) {
        dialogThumbs.forEach((thumb, index) => {
          if (!mask[index]) thumb.remove();
        });
      }

      slides.forEach((slide, index) => {
        if (!mask[index]) slide.remove();
      });
    }

    gallery.querySelectorAll('.product-media-container').forEach((container) => {
      if (container.closest('slideshow-slide, .dialog-zoomed-gallery')) return;
      const mediaId = container.querySelector('[data-media-id]')?.getAttribute('data-media-id');
      if (mediaId && colorByMediaId.has(mediaId)) {
        if (normalize(colorByMediaId.get(mediaId)) !== normalize(selectedColor)) container.remove();
        return;
      }
      const color = colorForImage(container.querySelector('img'), anchors);
      if (color && normalize(color) !== normalize(selectedColor)) container.remove();
    });

    const remainingSlides = [...gallery.querySelectorAll('slideshow-slide')];
    remainingSlides.forEach((slide, index) => {
      updateTimeline(slide, `--slide-${index}`);
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    });

    const slideshow = gallery.querySelector('slideshow-component');
    if (slideshow) {
      const timeline = remainingSlides.map((_, index) => `--slide-${index}`).join(', ');
      if (timeline) updateTimeline(slideshow, timeline);
      slideshow.setAttribute('initial-slide', '0');
      if (remainingSlides.length <= 1) {
        slideshow.classList.add('product-media-gallery__slideshow--single-media', 'slideshow--single-media');
      }
    }

    const arrows = gallery.querySelector('slideshow-arrows');
    if (arrows) arrows.hidden = remainingSlides.length <= 1;

    const thumbButtons = [...gallery.querySelectorAll('.slideshow-controls__thumbnail')];
    reindexHandlers(thumbButtons);
    thumbButtons.forEach((button, index) => {
      button.setAttribute('aria-label', `Slide ${index + 1} de ${thumbButtons.length}`);
      if (index === 0) button.setAttribute('aria-selected', 'true');
      else button.removeAttribute('aria-selected');
    });

    reindexHandlers([...gallery.querySelectorAll('slideshow-controls button')]);
    reindexHandlers(remainingSlides);
    reindexHandlers([...gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail')]);

    gallery.setAttribute('data-laeva-ready', 'true');
    gallery.setAttribute('data-laeva-color', selectedColor);
  };

  const apply = (root) => {
    const galleries = [...root.querySelectorAll('media-gallery')];
    galleries.forEach((gallery) => {
      const config = readConfig(root, gallery);
      if (!config) {
        gallery.setAttribute('data-laeva-ready', 'true');
        return;
      }
      hydrateFromDom(config, root);
      filterGallery(gallery, config);
    });

    if (root === document) {
      document.querySelectorAll('[data-laeva-media-pending]').forEach((node) => node.remove());
    }
  };

  if (!window.__laevaVariantMediaBound) {
    window.__laevaVariantMediaBound = true;
    document.addEventListener(
      'shopify:product:select',
      (event) => {
        const promise = event.promise;
        if (!promise || typeof promise.then !== 'function') return;
        promise
          .then((payload) => {
            const html = payload?.detail?.html;
            if (html) apply(html);
          })
          .catch(() => {});
      },
      true
    );
  }

  apply(document);
  window.__laevaVariantMediaLoaded = true;
})();
